import { tr } from '../i18n/i18n.ts';
import { useSyncExternalStore } from 'react';
import type { AppState, DayTemplate, ImportSource, LocalEvent, Overlay, SourceItem } from '@autoday/core';
import { api, ApiError } from '../api.ts';
import type { AuthStore } from '../auth/authStore.ts';
import type { AppStore } from '../store/appStore.ts';
import type { OutboxOp } from '../store/storage.ts';
import { platform, randomId } from '../platform.ts';
import type { PullResponse, PushOp, PushResult, SyncEntity } from './protocol.ts';
import { RemotePushConnector } from '../notifications/remotePush.ts';
import type { ServerConfig } from '../auth/authClient.ts';

export interface SyncConflict {
  entity: SyncEntity;
  id: string;
  title: string;
  localDoc: unknown;
  serverDoc: unknown;
  serverRevision: number;
  serverDeleted: boolean;
  at: string;
}

export interface SyncSnapshot {
  /**
   * local-only: gästprofil. paused: pausad (inför utloggning eller utan verifierad session ännu).
   * idle: senaste synk BEKRÄFTAD av servern. syncing / offline / error / auth-required (sessionen upphörd).
   */
  state: 'local-only' | 'paused' | 'idle' | 'syncing' | 'offline' | 'error' | 'auth-required';
  pending: number;
  conflicts: SyncConflict[];
  lastSyncAt: string | null;
  lastError: string | null;
}

/**
 * Lokal ändring sparas först (med utkorgspost i samma transaktion) och synkas sedan. Konflikter visas
 * – tyst last-write-wins förekommer inte för aktiviteter, överlägg eller källposter.
 * Synk körs bara i en kontoprofil, bara med verifierad session, och laddar aldrig upp gästdata.
 */
export class SyncClient {
  private snap: SyncSnapshot = { state: 'local-only', pending: 0, conflicts: [], lastSyncAt: null, lastError: null };
  private listeners = new Set<() => void>();
  private running: Promise<void> | null = null;
  private paused = false;
  private started = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private debounce: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private store: AppStore,
    private auth: AuthStore,
  ) {
    store.onCommitted.push(() => {
      if (!this.store.syncEnabled) return;
      void this.refreshPending();
      if (this.debounce) clearTimeout(this.debounce);
      this.debounce = setTimeout(() => void this.syncNow(), 1500);
    });
    auth.beforeSignOut.push(async () => {
      await this.pause();
    });
  }

  get snapshot() {
    return this.snap;
  }
  subscribe = (l: () => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };
  private emit(p: Partial<SyncSnapshot>) {
    const prevConflicts = this.snap.conflicts;
    this.snap = { ...this.snap, ...p };
    for (const l of this.listeners) l();
    if (p.conflicts && p.conflicts !== prevConflicts) void this.store.updateDevice((d) => ({ ...d, syncConflicts: p.conflicts! })).catch(() => undefined);
  }

  async start() {
    if (this.store.device.syncConflicts?.length) this.snap = { ...this.snap, conflicts: this.store.device.syncConflicts };
    if (!this.store.syncEnabled) {
      this.emit({ state: 'local-only' });
      return;
    }
    this.emit({ state: 'paused', lastSyncAt: this.store.device.lastSyncAt });
    await this.refreshPending();
    window.addEventListener('online', () => void this.syncNow());
    const onAuth = () => {
      const st = this.auth.state.status;
      if (st === 'signed-in') this.startTimers();
      if (st === 'expired') {
        this.stopTimers();
        this.emit({ state: 'auth-required' });
      }
      if (st === 'offline') this.emit({ state: 'offline' });
    };
    this.auth.subscribe(onAuth);
    onAuth();
  }

  private startTimers() {
    if (this.started || this.paused) return;
    this.started = true;
    this.timer = setInterval(() => void this.syncNow(), 60_000);
    void this.syncNow();
  }

  private stopTimers() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    if (this.debounce) clearTimeout(this.debounce);
    this.started = false;
  }

  /** Pausar nya synkoperationer och väntar in en pågående (inför utloggning). */
  async pause(): Promise<void> {
    this.paused = true;
    this.stopTimers();
    await this.running?.catch(() => undefined);
    this.emit({ state: 'paused' });
  }

  /** Återuppta efter "Avbryt" i utloggningsdialogen – befintligt tillstånd behålls. */
  resume() {
    this.paused = false;
    const st = this.auth.state.status;
    if (st === 'signed-in') this.startTimers();
    else this.emit({ state: st === 'expired' ? 'auth-required' : 'offline' });
  }

  async refreshPending(): Promise<number> {
    const n = (await this.store.storageAdapter.outbox()).length;
    if (n !== this.snap.pending) this.emit({ pending: n });
    return n;
  }

  /** Ändringar som servern inte bekräftat: utkorg + olösta konflikter. */
  async unsyncedCount(): Promise<number> {
    return (await this.refreshPending()) + this.snap.conflicts.length;
  }

  /** "Synka först": kör en synk även när nya operationer är pausade. */
  async flush(): Promise<{ ok: boolean; pending: number; conflicts: number; error?: string }> {
    await this.syncNow(true);
    const pending = await this.refreshPending();
    const conflicts = this.snap.conflicts.length;
    const ok = this.snap.state === 'idle' && pending === 0 && conflicts === 0;
    if (this.paused) this.emit({ state: ok ? 'paused' : this.snap.state });
    return {
      ok,
      pending,
      conflicts,
      ...(ok ? {} : { error: conflicts ? tr('Det finns synkkonflikter att lösa först.', 'There are sync conflicts to resolve first.') : this.snap.state === 'offline' ? tr('Ingen kontakt med servern.', 'No contact with the server.') : (this.snap.lastError ?? tr('Synken blev inte klar.', 'The sync did not finish.')) }),
    };
  }

  async syncNow(force = false): Promise<void> {
    if (!this.store.syncEnabled || (this.paused && !force)) return;
    const st = this.auth.state.status;
    if (st !== 'signed-in' && st !== 'offline') return;
    if (this.running) return this.running;
    if (!navigator.onLine) {
      this.emit({ state: 'offline', pending: (await this.store.storageAdapter.outbox()).length });
      return;
    }
    this.running = this.runOnce().finally(() => {
      this.running = null;
    });
    return this.running;
  }

  private async runOnce(): Promise<void> {
    this.emit({ state: 'syncing' });
    try {
      await this.ensureDevice();
      await this.push();
      await this.pull();
      await this.ackLocalSchedule();
      // Fjärrpush som reserv (avstängd tills servern har FCM/APNs). Fel här stoppar aldrig synken.
      void this.remotePush()?.ensure();
      const now = new Date().toISOString();
      await this.store.updateDevice((d) => ({ ...d, lastSyncAt: now, lastSyncError: null }));
      this.emit({ state: 'idle', lastSyncAt: now, lastError: null, pending: (await this.store.storageAdapter.outbox()).length });
      if (this.auth.state.status === 'offline') void this.auth.verify();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      const st = e instanceof ApiError && e.status === 401 ? 'auth-required' : e instanceof ApiError && (e.code === 'NETWORK' || e.code === 'TIMEOUT') ? 'offline' : 'error';
      await this.store.updateDevice((d) => ({ ...d, lastSyncError: msg }));
      this.emit({ state: st, lastError: msg, pending: (await this.store.storageAdapter.outbox()).length });
      // 401 = sessionen har verkligen upphört. ACCOUNT_MISMATCH = cookien tillhör ett annat konto.
      if (e instanceof ApiError && e.status === 401) {
        this.stopTimers();
        this.auth.markExpired();
      }
      if (e instanceof ApiError && e.code === 'ACCOUNT_MISMATCH') {
        this.stopTimers();
        void this.auth.handleAccountMismatch();
      }
    }
  }

  private async ensureDevice() {
    const dev = this.store.device;
    if (dev.deviceRegistered) return;
    await api(`/api/devices/${encodeURIComponent(dev.deviceId)}`, { method: 'PUT', body: { platform: platform(), name: navigator.userAgent.slice(0, 120) } });
    await this.store.updateDevice((d) => ({ ...d, deviceRegistered: true }));
  }

  private async push() {
    const outbox = await this.store.storageAdapter.outbox();
    if (!outbox.length) return;
    // Slå ihop flera ändringar av samma post: skicka senaste dokumentet med äldsta basrevisionen.
    const byKey = new Map<string, { op: OutboxOp; seqs: number[] }>();
    for (const o of outbox) {
      const k = `${o.entity}:${o.id}`;
      const cur = byKey.get(k);
      if (!cur) byKey.set(k, { op: o, seqs: [o.seq!] });
      else byKey.set(k, { op: { ...o, baseRevision: cur.op.baseRevision }, seqs: [...cur.seqs, o.seq!] });
    }
    const groups = [...byKey.values()];
    const ops: PushOp[] = groups.map((g) => ({
      opId: g.op.opId,
      entity: g.op.entity,
      id: g.op.id,
      baseRevision: this.serverRevision(g.op.entity, g.op.id),
      doc: g.op.doc,
      deleted: !!g.op.deleted || !!(g.op.doc as { deleted?: boolean } | null)?.deleted,
      ...(g.op.importId ? { importId: g.op.importId } : {}),
    }));
    for (let i = 0; i < ops.length; i += 200) {
      const slice = ops.slice(i, i + 200);
      const res = await api<{ results: PushResult[] }>('/api/sync/push', { body: { deviceId: this.store.device.deviceId, ops: slice }, timeoutMs: 30000 });
      await this.handleResults(slice, res.results);
      const handled = new Set(res.results.map((r) => r.opId));
      const remove: number[] = [];
      for (const g of groups) if (handled.has(g.op.opId)) remove.push(...g.seqs);
      await this.store.storageAdapter.removeOutbox(remove);
    }
  }

  private serverRevision(entity: SyncEntity, id: string): number | null {
    return this.knownServerRevisions()[`${entity}:${id}`] ?? null;
  }
  private knownServerRevisions(): Record<string, number> {
    return this.store.device.serverRevisions ?? {};
  }

  private async handleResults(ops: PushOp[], results: PushResult[]) {
    // Bara de egna uppdateringarna slås ihop med det som är sparat (en annan flik kan ha hunnit längre).
    const revs: Record<string, number> = {};
    const conflicts: SyncConflict[] = [...this.snap.conflicts];
    for (const r of results) {
      const op = ops.find((o) => o.opId === r.opId);
      if (!op) continue;
      const k = `${op.entity}:${op.id}`;
      if (r.status === 'applied' || r.status === 'duplicate' || r.status === 'converged') revs[k] = r.revision;
      if (r.status === 'conflict') {
        const existing = conflicts.findIndex((x) => x.entity === op.entity && x.id === op.id);
        if (existing >= 0) conflicts.splice(existing, 1);
        conflicts.push({ entity: op.entity, id: op.id, title: titleOf(op.entity, op.doc), localDoc: op.doc, serverDoc: r.serverDoc, serverRevision: r.serverRevision, serverDeleted: r.serverDeleted, at: new Date().toISOString() });
      }
      if (r.status === 'rejected') {
        await this.store.updateDevice((d) => ({ ...d, observedErrors: [...d.observedErrors, { at: new Date().toISOString(), channel: 'sync', message: `${r.message} (${op.entity})` }].slice(-30) }));
      }
    }
    await this.store.updateDevice((d) => ({ ...d, serverRevisions: { ...d.serverRevisions, ...revs } }));
    this.emit({ conflicts });
  }

  private async pull() {
    let cursor = this.store.device.syncCursor;
    for (let guard = 0; guard < 50; guard++) {
      const res = await api<PullResponse>(`/api/sync/pull?since=${cursor}`);
      if (res.changes.length) {
        // Osynkade lokala ändringar OCH olösta konflikter skrivs aldrig över av en pull.
        const seen = this.store.state;
        const pendingKeys = new Set([...(await this.store.storageAdapter.outbox()).map((o) => `${o.entity}:${o.id}`), ...this.snap.conflicts.map((c) => `${c.entity}:${c.id}`)]);
        const revs: Record<string, number> = {};
        const conflicts: SyncConflict[] = [...this.snap.conflicts];
        await this.store.replace(
          (s) => {
            // Kan köras om på färskare data (en annan flik skrev emellan) – börja då från början.
            for (const k of Object.keys(revs)) delete revs[k];
            let next: AppState = s;
            for (const c of res.changes) {
              const k = `${c.entity}:${c.id}`;
              // Ändrad lokalt efter att utkorgen lästes (sparad medan svaret behandlades): räknas som
              // osynkad. Basrevisionen lämnas orörd, så att servern visar en konflikt vid nästa push.
              if (pendingKeys.has(k) || changedLocally(s, seen, c.entity, c.id)) {
                const idx = conflicts.findIndex((x) => x.entity === c.entity && x.id === c.id);
                if (idx >= 0) conflicts[idx] = { ...conflicts[idx]!, serverDoc: c.doc, serverRevision: c.revision, serverDeleted: c.deleted };
                continue;
              }
              next = applyRemote(next, c.entity, c.id, c.doc, c.deleted);
              revs[k] = c.revision;
            }
            return next;
          },
          { fromRemote: true },
        );
        await this.store.updateDevice((d) => ({ ...d, serverRevisions: { ...d.serverRevisions, ...revs } }));
        this.emit({ conflicts });
      }
      cursor = res.cursor;
      await this.store.updateDevice((d) => ({ ...d, syncCursor: Math.max(d.syncCursor, cursor) }));
      if (!res.more) break;
    }
  }

  private remote: RemotePushConnector | null = null;

  /** Native: registrera fjärrpush-token hos servern om servern stöder det. Webb: null. */
  private remotePush(): RemotePushConnector | null {
    const os = platform();
    if (os === 'web' || this.auth.profile.kind !== 'account') return null;
    const deviceId = this.store.device.deviceId;
    this.remote ??= new RemotePushConnector({
      os,
      accountTag: this.auth.profile.accountTag ?? 'unbound',
      serverFeatures: async () => (await api<ServerConfig>('/api/config', { timeoutMs: 8000 })).features.nativePush,
      putToken: (provider, token) => api(`/api/devices/${encodeURIComponent(deviceId)}/native-push`, { method: 'PUT', body: { provider, token } }).then(() => undefined),
      onOpened: (deliveryId) => {
        void api('/api/notifications/opened', { body: { deliveryId } }).catch(() => undefined);
        // Innehållet finns inte i fjärrpushen – det kommer med synken.
        window.location.hash = '#/idag';
        void this.syncNow(true);
      },
      onForeign: () => {
        window.location.hash = '#/idag';
      },
    });
    return this.remote;
  }

  /** Native: berätta för servern vilka larmpunkter enheten själv schemalagt → servern skickar inte dubbelt. */
  private async ackLocalSchedule() {
    if (platform() === 'web') return;
    const plan = this.store.device.lastPlan;
    if (!plan) return;
    await api(`/api/devices/${encodeURIComponent(this.store.device.deviceId)}/local-schedule`, {
      method: 'PUT',
      body: { pointIds: plan.scheduled.flatMap((n) => n.pointIds), coverageUntil: plan.coverageUntil, generatedAt: plan.generatedAt },
    });
  }

  /** Användarens beslut i en konflikt. */
  async resolveConflict(c: SyncConflict, choice: 'mine' | 'server') {
    const k = `${c.entity}:${c.id}`;
    if (choice === 'server') {
      await this.store.replace((s) => applyRemote(s, c.entity, c.id, c.serverDoc, c.serverDeleted), { fromRemote: true });
      await this.store.updateDevice((d) => ({ ...d, serverRevisions: { ...d.serverRevisions, [k]: c.serverRevision } }));
    } else {
      const ops: PushOp[] = [{ opId: randomId('op_'), entity: c.entity, id: c.id, baseRevision: c.serverRevision, doc: c.localDoc }];
      const res = await api<{ results: PushResult[] }>('/api/sync/push', { body: { deviceId: this.store.device.deviceId, ops } });
      await this.handleResults(ops, res.results);
    }
    this.emit({ conflicts: this.snap.conflicts.filter((x) => x !== c) });
  }
}

/** Har posten ändrats lokalt mellan två tillstånd? (Objektidentitet – domänen ändrar alltid oföränderligt.) */
function changedLocally(a: AppState, b: AppState, entity: SyncEntity, id: string): boolean {
  switch (entity) {
    case 'event':
      return a.events[id] !== b.events[id];
    case 'overlay':
      return a.overlays[id] !== b.overlays[id];
    case 'template':
      return a.templates[id] !== b.templates[id];
    case 'sourceItem':
      return a.sourceItems[id] !== b.sourceItems[id];
    case 'importSource':
      return a.sources[id] !== b.sources[id];
    case 'meta':
      return a.settings !== b.settings || a.calendars !== b.calendars || a.categories !== b.categories;
  }
}

function titleOf(entity: SyncEntity, doc: unknown): string {
  if (entity === 'meta') return tr('Inställningar och kalendrar', 'Settings and calendars');
  const d = doc as { title?: string; data?: { summary?: string }; key?: string; name?: string } | null;
  return d?.title ?? d?.data?.summary ?? d?.name ?? d?.key ?? tr('Post', 'Item');
}

export function applyRemote(s: AppState, entity: SyncEntity, id: string, doc: unknown, deleted: boolean): AppState {
  switch (entity) {
    case 'event':
      return { ...s, events: { ...s.events, [id]: { ...(doc as LocalEvent), ...(deleted ? { deleted: true } : {}) } } };
    case 'overlay': {
      const o = { ...s.overlays };
      if (deleted) delete o[id];
      else o[id] = doc as Overlay;
      return { ...s, overlays: o };
    }
    case 'template': {
      const t = { ...s.templates };
      if (deleted) delete t[id];
      else t[id] = doc as DayTemplate;
      return { ...s, templates: t };
    }
    case 'sourceItem': {
      const si = { ...s.sourceItems };
      if (deleted) delete si[id];
      else si[id] = doc as SourceItem;
      return { ...s, sourceItems: si };
    }
    case 'importSource':
      return { ...s, sources: { ...s.sources, [id]: doc as ImportSource } };
    case 'meta': {
      const m = doc as Pick<AppState, 'settings' | 'calendars' | 'categories'>;
      return { ...s, settings: m.settings ?? s.settings, calendars: m.calendars ?? s.calendars, categories: m.categories ?? s.categories };
    }
  }
}

let client: SyncClient | null = null;
export function setSyncClient(c: SyncClient) {
  client = c;
}
export function getSyncClient(): SyncClient {
  if (!client) throw new Error('Synk ej initierad');
  return client;
}
export function useSync(): SyncSnapshot {
  const c = getSyncClient();
  return useSyncExternalStore(c.subscribe, () => c.snapshot);
}
